import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserRole, UserProfile } from '../types/user.types';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface UserWithId extends UserProfile {
  id: string;
}

const AdminDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<UserWithId | null>(null);
  const [newRole, setNewRole] = useState<UserRole>(UserRole.RINGER);
  const [newAuctionHouseName, setNewAuctionHouseName] = useState('');

  useEffect(() => {
    // Check if user is admin
    if (userProfile?.role !== UserRole.ADMIN) {
      toast.error('Access denied: Admin only');
      navigate('/');
      return;
    }
    
    loadUsers();
  }, [userProfile, navigate]);

  const loadUsers = async () => {
    try {
      console.log('Loading users from Firestore...');
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      console.log('Snapshot size:', snapshot.size);
      console.log('Snapshot empty:', snapshot.empty);
      
      const usersList: UserWithId[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        console.log('User document:', docSnap.id, data);
        
        // Create user object with safe defaults
        const user: UserWithId = {
          id: docSnap.id,
          uid: data.uid || docSnap.id, // Use document ID if uid is missing
          email: data.email || 'No email',
          role: data.role || UserRole.RINGER,
          displayName: data.displayName || data.email?.split('@')[0] || 'Unknown',
          auctionHouseName: data.auctionHouseName,
          auctionHouseId: data.auctionHouseId,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now())
        };
        
        usersList.push(user);
      });

      console.log('Users loaded:', usersList.length);

      // Sort by creation date (newest first)
      usersList.sort((a, b) => {
        const aTime = a.createdAt?.getTime() || 0;
        const bTime = b.createdAt?.getTime() || 0;
        return bTime - aTime;
      });

      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users: ' + (error as any).message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: UserWithId) => {
    setEditingUser(user);
    setNewRole(user.role);
    setNewAuctionHouseName(user.auctionHouseName || '');
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      const userRef = doc(db, 'users', editingUser.id);
      const updateData: any = {
        role: newRole,
        updatedAt: new Date()
      };

      // If changing to auctioneer, require auction house name
      if (newRole === UserRole.AUCTIONEER) {
        if (!newAuctionHouseName) {
          toast.error('Auction house name is required for auctioneers');
          return;
        }
        updateData.auctionHouseName = newAuctionHouseName;
        
        // Create auction house ID if it doesn't exist
        if (!editingUser.auctionHouseId) {
          updateData.auctionHouseId = `ah_${Date.now()}`;
        }
      } else {
        // If changing away from auctioneer, remove auction house data
        updateData.auctionHouseName = null;
        updateData.auctionHouseId = null;
      }

      await updateDoc(userRef, updateData);
      
      toast.success('User updated successfully');
      setEditingUser(null);
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const getRoleBadge = (role: UserRole) => {
    const colors = {
      [UserRole.ADMIN]: 'bg-red-100 text-red-800',
      [UserRole.AUCTIONEER]: 'bg-purple-100 text-purple-800',
      [UserRole.RINGER]: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors[role]}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage users and system settings</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Total Users</h3>
            <span className="text-2xl">👥</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">{users.length}</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Ringers</h3>
            <span className="text-2xl">🎯</span>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {users.filter(u => u.role === UserRole.RINGER).length}
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Auctioneers</h3>
            <span className="text-2xl">🔨</span>
          </div>
          <p className="text-3xl font-bold text-purple-600">
            {users.filter(u => u.role === UserRole.AUCTIONEER).length}
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Admins</h3>
            <span className="text-2xl">⚙️</span>
          </div>
          <p className="text-3xl font-bold text-red-600">
            {users.filter(u => u.role === UserRole.ADMIN).length}
          </p>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">All Users</h2>
        
        {users.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auction House
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.email}</div>
                      <div className="text-sm text-gray-500">
                        ID: {user.uid ? user.uid.slice(0, 8) + '...' : user.id.slice(0, 8) + '...'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.auctionHouseName ? (
                        <div className="text-sm text-gray-900">{user.auctionHouseName}</div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt ? user.createdAt.toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-primary-600 hover:text-primary-900 font-semibold"
                      >
                        Edit Role
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Edit User Role</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Email</p>
              <p className="font-semibold">{editingUser.email}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as UserRole)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value={UserRole.RINGER}>Ringer</option>
                <option value={UserRole.AUCTIONEER}>Auctioneer</option>
                <option value={UserRole.ADMIN}>Admin</option>
              </select>
            </div>

            {newRole === UserRole.AUCTIONEER && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auction House Name
                </label>
                <input
                  type="text"
                  value={newAuctionHouseName}
                  onChange={(e) => setNewAuctionHouseName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter auction house name"
                />
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setEditingUser(null)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="flex-1 btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;